/**
 * useAnalyticsHandlers Hook
 * 
 * Handles all event handlers for the Analytics page:
 * - Period selection and navigation
 * - User filter handlers
 * - Account filter handlers
 * - Filter clearing
 */

import { useCallback, useMemo } from "react";
import { MouseEvent } from "react";
import { WorkspaceMember } from "./useAnalyticsWorkspaceMembers";
import { Account } from "../../../types";

export interface UseAnalyticsHandlersParams {
  // State setters
  setSelectedPeriod: (period: string) => void;
  setCurrentPeriodOffset: (offset: number) => void;
  setSelectedUsers: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedAccounts: React.Dispatch<React.SetStateAction<string[]>>;
  setUserFilterAnchor: (anchor: HTMLElement | null) => void;
  setAccountFilterAnchor: (anchor: HTMLElement | null) => void;
  setFromDate: (date: string) => void;
  setToDate: (date: string) => void;
  // Current state values
  selectedPeriod: string;
  currentPeriodOffset: number;
  selectedUsers: string[];
  selectedAccounts: string[];
  workspaceMembers: WorkspaceMember[];
  accounts: Account[];
  fromDate: string;
  toDate: string;
  // Helper functions
  setDateRangeByPeriod: (periodType: string, offset: number) => void;
  // Data
  transactions: Array<{ userId?: string }>;
}

export interface UseAnalyticsHandlersReturn {
  // Period handlers
  handlePeriodChange: (period: string) => void;
  handlePeriodNavigation: (direction: "left" | "right") => void;
  // User filter handlers
  handleUserFilterClick: (event: MouseEvent<HTMLElement>) => void;
  handleUserFilterClose: () => void;
  handleUserToggle: (userId: string) => void;
  handleSelectAllUsers: () => void;
  // Account filter handlers
  handleAccountFilterClick: (event: MouseEvent<HTMLElement>) => void;
  handleAccountFilterClose: () => void;
  handleAccountToggle: (accountId: string) => void;
  handleSelectAllAccounts: () => void;
  // Filter utilities
  hasActiveFilters: boolean;
  clearAllFilters: () => void;
}

/**
 * Hook for Analytics event handlers
 */
export const useAnalyticsHandlers = ({
  setSelectedPeriod,
  setCurrentPeriodOffset,
  setSelectedUsers,
  setSelectedAccounts,
  setUserFilterAnchor,
  setAccountFilterAnchor,
  setFromDate,
  setToDate,
  selectedPeriod,
  currentPeriodOffset,
  selectedUsers,
  selectedAccounts,
  workspaceMembers,
  accounts,
  fromDate,
  toDate,
  setDateRangeByPeriod,
  transactions,
}: UseAnalyticsHandlersParams): UseAnalyticsHandlersReturn => {
  // Handle period selection
  const handlePeriodChange = (period: string): void => {
    setSelectedPeriod(period);
    setCurrentPeriodOffset(0);
    if (period === "Custom") {
      // Don't set dates automatically for custom period - let user select manually
      return;
    }
    if (period === "All time") {
      // For "All time", we need to wait for workspace creation date if not loaded yet
      // The setDateRangeByPeriod will handle it when workspaceCreatedAt is available
      setDateRangeByPeriod(period, 0);
      return;
    }
    setDateRangeByPeriod(period, 0);
  };

  // Handle navigation (left/right arrows)
  const handlePeriodNavigation = (direction: "left" | "right"): void => {
    if (!selectedPeriod || selectedPeriod === "All time") return;
    
    const newOffset = direction === "left" 
      ? currentPeriodOffset - 1 
      : currentPeriodOffset + 1;
    
    setCurrentPeriodOffset(newOffset);
    setDateRangeByPeriod(selectedPeriod, newOffset);
  };

  // Check if any filters are active (only show button when user has changed filters from defaults)
  const hasActiveFilters = useMemo(() => {
    // If dates are not set yet, don't show button (still loading)
    if (!fromDate || !toDate) {
      return false;
    }
    
    // Check if all users are selected (default state) - empty array or all users means no filter
    const allUsersSelected = workspaceMembers.length > 0 && 
      (selectedUsers.length === 0 || selectedUsers.length === workspaceMembers.length);
    
    // Check if all accounts are selected (default state) - empty array or all accounts means no filter
    const allAccountsSelected = accounts.length > 0 && 
      (selectedAccounts.length === 0 || selectedAccounts.length === accounts.length);
    
    // Check if dates match default Monthly period (current month)
    // Use same format as Analytics.tsx (local date strings, not ISO to avoid timezone issues)
    const today = new Date();
    const defaultFrom = new Date(today.getFullYear(), today.getMonth(), 1);
    const defaultTo = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const defaultFromStr = `${defaultFrom.getFullYear()}-${String(defaultFrom.getMonth() + 1).padStart(2, '0')}-${String(defaultFrom.getDate()).padStart(2, '0')}`;
    const defaultToStr = `${defaultTo.getFullYear()}-${String(defaultTo.getMonth() + 1).padStart(2, '0')}-${String(defaultTo.getDate()).padStart(2, '0')}`;
    const datesMatchDefault = fromDate === defaultFromStr && toDate === defaultToStr;
    
    // Check if period offset is at default (0)
    const periodOffsetIsDefault = currentPeriodOffset === 0;
    
    // Only show button if filters differ from defaults
    return (
      selectedPeriod !== "Monthly" ||
      !periodOffsetIsDefault ||
      (accounts.length > 0 && !allAccountsSelected) ||
      (workspaceMembers.length > 1 && !allUsersSelected) ||
      !datesMatchDefault
    );
  }, [selectedPeriod, currentPeriodOffset, selectedAccounts.length, accounts.length, selectedUsers.length, workspaceMembers.length, fromDate, toDate]);

  // Clear all filters
  const clearAllFilters = (): void => {
    setSelectedPeriod("Monthly");
    setCurrentPeriodOffset(0);
    setSelectedAccounts([]); // Empty array = show all accounts (no filter)
    setSelectedUsers([]); // Empty array = show all users (no filter)
    // Set default Monthly period dates (use local format to match hasActiveFilters check)
    const today = new Date();
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const formatLocalDate = (d: Date): string =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    setFromDate(formatLocalDate(from));
    setToDate(formatLocalDate(to));
  };

  // User filter handlers
  const handleUserFilterClick = (event: MouseEvent<HTMLElement>): void => {
    console.log("[Analytics] handleUserFilterClick called", { 
      currentTarget: event.currentTarget,
      currentTargetTag: event.currentTarget?.tagName,
      currentTargetId: event.currentTarget?.id,
      currentTargetClassName: event.currentTarget?.className,
      setUserFilterAnchor: typeof setUserFilterAnchor
    });
    try {
      setUserFilterAnchor(event.currentTarget);
      console.log("[Analytics] User filter anchor set to:", event.currentTarget, "Type:", event.currentTarget?.constructor?.name);
      // Verify it was set by checking in next tick
      setTimeout(() => {
        console.log("[Analytics] Verifying anchor was set (check state in parent component)");
      }, 0);
    } catch (error) {
      console.error("[Analytics] Error setting user filter anchor:", error);
    }
  };

  const handleUserFilterClose = (): void => {
    setUserFilterAnchor(null);
  };

  const handleUserToggle = useCallback((userId: string): void => {
    // Simple toggle logic - same as account filter
    setSelectedUsers((prev) => {
      const prevSet = new Set(prev);
      if (prevSet.has(userId)) {
        prevSet.delete(userId);
        return Array.from(prevSet);
      } else {
        prevSet.add(userId);
        return Array.from(prevSet);
      }
    });
  }, [setSelectedUsers]);

  const handleSelectAllUsers = useCallback((): void => {
    // Filter out orphaned user IDs and fake users (same logic as UserFilterPopover)
    const isOrphanedUserId = (userId: string): boolean => userId.startsWith("Ao7fhBrt");
    const isFakeUserId = (userId: string): boolean => {
      return ["demo-user"].includes(userId); // Removed dev-user-developer - we use bypass-user now
    };
    const filteredMembers = workspaceMembers.filter(user => 
      !isOrphanedUserId(user.id) && !isFakeUserId(user.id)
    );
    const filteredSelectedUsers = selectedUsers.filter(id => 
      !isOrphanedUserId(id) && !isFakeUserId(id)
    );
    
    setSelectedUsers((prev) => {
      // Filter current selection
      const currentFiltered = prev.filter(id => 
        !isOrphanedUserId(id) && !isFakeUserId(id)
      );
      
      // Toggle: if all filtered members are selected, clear to empty (show all)
      // Otherwise, select all filtered members
      if (currentFiltered.length === filteredMembers.length) {
        return []; // Empty array = show all (no filter)
      } else {
        return filteredMembers.map((u) => u.id);
      }
    });
  }, [workspaceMembers, selectedUsers, setSelectedUsers]);

  // Account filter handlers
  const handleAccountFilterClick = (event: MouseEvent<HTMLElement>): void => {
    setAccountFilterAnchor(event.currentTarget);
  };

  const handleAccountFilterClose = (): void => {
    setAccountFilterAnchor(null);
  };

  const handleAccountToggle = useCallback((accountId: string): void => {
    setSelectedAccounts((prev) => {
      // Use Set for O(1) lookup performance
      const prevSet = new Set(prev);
      if (prevSet.has(accountId)) {
        prevSet.delete(accountId);
        return Array.from(prevSet);
      } else {
        prevSet.add(accountId);
        return Array.from(prevSet);
      }
    });
  }, [setSelectedAccounts]);

  const handleSelectAllAccounts = useCallback((): void => {
    setSelectedAccounts((prev) => {
      if (prev.length === accounts.length) {
        return [];
      } else {
        return accounts.map((acc) => acc.id);
      }
    });
  }, [accounts.length, setSelectedAccounts]);

  return {
    handlePeriodChange,
    handlePeriodNavigation,
    handleUserFilterClick,
    handleUserFilterClose,
    handleUserToggle,
    handleSelectAllUsers,
    handleAccountFilterClick,
    handleAccountFilterClose,
    handleAccountToggle,
    handleSelectAllAccounts,
    hasActiveFilters,
    clearAllFilters,
  };
};
